import React, { useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SectionHeader = ({
    badgeLabel,
    badgeCount,
    badgeUnit = 'Items',
    badgeIcon,
    pulseColor = 'bg-emerald-500',
    title,
    subtitle
}) => {
    const { theme, themes } = useContext(ThemeContext);
    const isLight = themes[theme]?.isIconDark; // true when theme has light background

    // Formats count as 2-digit (e.g., 8 -> "08")
    const formattedCount = badgeCount !== undefined
        ? String(badgeCount).padStart(2, '0')
        : null;

    return (
        <header className="w-full max-w-4xl mx-auto mb-10 flex flex-col items-center text-center select-none">
            {/* Theme-Adaptive Micro-Pill Badge */}
            <div 
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border backdrop-blur-md mb-3 transition-colors duration-300 ${
                    isLight 
                        ? 'border-black/15 bg-black/[0.04] shadow-sm' 
                        : 'border-white/10 bg-white/[0.04] shadow-[0_0_15px_rgba(255,255,255,0.05)]'
                }`}
            >
                {badgeIcon ? (
                    <FontAwesomeIcon icon={badgeIcon} className="text-amber-500 text-[10px]" />
                ) : (
                    <span className={`w-1.5 h-1.5 rounded-full ${pulseColor} animate-pulse`} />
                )}

                <span className={`text-[11px] font-mono tracking-widest uppercase font-semibold ${themes[theme].text} opacity-80`}>
                    {badgeLabel}
                </span>

                {formattedCount && (
                    <>
                        <span className={`${themes[theme].text} opacity-30`}>•</span>
                        <span className={`text-[11px] font-mono ${themes[theme].text} opacity-60 font-medium`}>
                            {formattedCount} {badgeUnit}
                        </span>
                    </>
                )}
            </div>

            {/* Title: uses theme text with dynamic contrast */}
            <h1 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${themes[theme].text}`}>
                {title}
            </h1>

            {/* Subtitle: uses theme subtext */}
            {subtitle && (
                <p className={`text-xs sm:text-sm mt-1.5 tracking-wide max-w-lg leading-relaxed ${themes[theme].subText}`}>
                    {subtitle}
                </p>
            )}

            {/* Bottom accent divider line that adapts to theme */}
            <div 
                className={`w-16 h-[1px] mt-4 ${
                    isLight 
                        ? 'bg-gradient-to-r from-transparent via-black/20 to-transparent' 
                        : 'bg-gradient-to-r from-transparent via-white/20 to-transparent'
                }`} 
            />
        </header>
    );
};

export default SectionHeader;